import { Badge } from "../../components/Badge";
import {
  LoaderFunctionArgs,
  useLoaderData,
  useNavigate,
  useRevalidator,
  useSearchParams,
} from "react-router-dom";
import { toast } from "react-hot-toast";
import { NewStripeConnectAccountModal } from "../stripe_connect/NewStripeConnectAccountModal";
import { TerminalLocationModal } from "../stripe_connect/TerminalLocationModal";
import { refreshAccountStatus } from "../stripe_connect/api";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { PAYMENT_PROCESSOR_LIST } from "./queries";
import { Link } from "react-router-dom";
import { PaymentProcessor, Events } from "../../types/paymentProcessor";
import {
  PROCESSOR_SEARCH_PLACEHOLDER,
  matchesProcessorSearch,
} from "./search";
import { useEffect, useMemo, useRef, useState } from "react";
import { organizationStore } from "../../store/organization";
import { eventStore } from "../../store/event";
import moment from "moment/moment";
import { usePermissions } from "../auth/api";
import {
  ColDef,
  GridApi,
  SizeColumnsToContentStrategy,
} from "ag-grid-community";
import { Breadcrumbs } from "../../components/Breadcrumbs";
import { Input } from "../../components/inputs/Input";
import { PrimaryButton, SecondaryButton } from "../../components/Button";
import { AgGridReact } from "ag-grid-react";
import Switch from "react-switch";

const client = createApolloClient();
const { getState } = organizationStore;
const { getState: getEventStore } = eventStore;

// NOTE: search is no longer part of the GraphQL where clause. It used to be a
// server-side `payment_processor _ilike '%…%'`, which meant the only way to
// find a row was by its name — useless when a merchant onboarded under their
// personal name instead of their business name. The connected account id and
// terminal location id live in the jsonb `config` column, which Hasura can
// only match exactly (`_contains`), so the filter now runs in the browser over
// the rows the loader already fetched. See ./search.ts.

type CellRenderer = (params: {
  value:
    | string
    | number
    | boolean
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>
    | React.ReactFragment
    | React.ReactPortal
    | null
    | undefined;
  data: PaymentProcessor;
}) => JSX.Element | null;
const createCellRenderer = (
  permission: boolean,
  linkFunc: (params: { data: PaymentProcessor }) => string
): CellRenderer => {
  return (params) => {
    if (params.value === undefined) {
      return null;
    }
    return (
      <>
        {permission ? (
          <Link to={linkFunc(params)}>
            <Badge>{params.value}</Badge>
          </Link>
        ) : (
          <Badge>{params.value}</Badge>
        )}
      </>
    );
  };
};
const createMultipleLinksCellRenderer = (
  permission: boolean,
  linkFunc: (event: Events) => string
): CellRenderer => {
  return (params) => {
    const events: Events[] = (params.data?.events || []) as unknown as Events[];
    const renderedEvents = events.map((event, index) => (
      <span key={index}>
        {permission ? (
          <Link to={linkFunc(event)}>
            <Badge>{event.name}</Badge>
          </Link>
        ) : (
          <Badge>{event.name}</Badge>
        )}
        {index < events.length - 1 && " "}
      </span>
    ));
    return <>{renderedEvents}</>;
  };
};

export const paymentProcessorListLoader = async (_args: LoaderFunctionArgs) => {
  const rawOrganizationId = getState().organizationId;
  const organizationId = parseInt(rawOrganizationId !== null ? rawOrganizationId.toString() : '', 10);
  const eventId = getEventStore().eventId;

  let where: any = {};
  if (organizationId && organizationId !== 0) {
    where = {
      ...where,
      organization_id: {
        _eq: organizationId,
      },
    };
  }
  // if (eventId) {
  //   where = {
  //     ...where,
  //     event_id: {
  //       _eq: eventId,
  //     },
  //   };
  // }

  const { data } = await client.query({
    query: PAYMENT_PROCESSOR_LIST,
    variables: {
      where,
    },
  });
  return data.payment_processor_config;
};

export const PaymentProcessorList = () => {
  const { editCreatePermission } = usePermissions("payment_processors");
  const { editCreatePermission: eventsProcessorEditPermission } =
    usePermissions("events");
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const gridRef = useRef<GridApi>();
  const processors = useLoaderData() as PaymentProcessor[];
  const [rowData, setRowData] = useState<PaymentProcessor[]>([]);
  const [isActiveToggle, setIsActiveToggle] = useState(true);
  // Stripe redirects back to ?onboarding=complete&processorId=N after the
  // merchant finishes hosted onboarding; ?search= may also arrive from a
  // bookmarked/shared link.
  const [searchParams, setSearchParams] = useSearchParams();
  // Seeded once from ?search= so old links still work, then kept purely local:
  // typing no longer pushes a route change, so we don't re-run the loader (and
  // re-query Hasura) on every keystroke.
  const [search, setSearch] = useState<string>(
    () => searchParams.get("search") || ""
  );
  // Hide Stripe processors that never finished connecting (no connected
  // account id) by default — these are in-progress/abandoned onboarding rows
  // that clutter the list. Non-Stripe processors (mxmerchant/braintree) don't
  // use a connected account, so they're always shown. Ops can flip this on to
  // see and clean up the unconnected rows.
  const [showUnconnected, setShowUnconnected] = useState(false);
  useEffect(() => {
    let items = processors;
    if (isActiveToggle) {
      items = items.filter((processor) => processor.is_active);
    }
    if (!showUnconnected) {
      items = items.filter(
        (processor) =>
          processor.processor !== "stripe" ||
          !!processor.config?.connected_account_id
      );
    }
    // Matches name, organization, connected account id, previous account id
    // and terminal location id — see ./search.ts.
    items = items.filter((processor) =>
      matchesProcessorSearch(processor, search)
    );
    setRowData(items);
  }, [processors, isActiveToggle, showUnconnected, search]);

  // After hosted onboarding Stripe sends the merchant back here. Pull the
  // latest status, then drop the params so a page reload doesn't re-fire the
  // request.
  const onboardingState = searchParams.get("onboarding");
  const returnProcessorId = searchParams.get("processorId");
  useEffect(() => {
    let cancelled = false;
    if (onboardingState === "complete" && returnProcessorId) {
      (async () => {
        try {
          await refreshAccountStatus(Number(returnProcessorId));
          if (!cancelled) toast.success("Stripe account status refreshed");
        } catch (err: any) {
          if (!cancelled) toast.error(`Status refresh failed: ${err.message || "unknown"}`);
        } finally {
          if (!cancelled) {
            setSearchParams({}, { replace: true });
            revalidator.revalidate();
          }
        }
      })();
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingState, returnProcessorId]);

  // If we just returned from onboarding and the processor has no Terminal
  // location yet, prompt for one. We look up the row from the loader data so
  // the modal already knows the processor's name + id.
  const [postOnboardLocationFor, setPostOnboardLocationFor] = useState<{
    id: number;
    name: string;
  } | null>(null);
  useEffect(() => {
    if (onboardingState !== "complete" || !returnProcessorId) return;
    const idNum = Number(returnProcessorId);
    const row = processors.find((p: any) => p.id === idNum);
    if (!row) return;
    const hasAccount = !!row?.config?.connected_account_id;
    const hasLocation = !!row?.config?.platform_account_location_id;
    if (hasAccount && !hasLocation) {
      setPostOnboardLocationFor({ id: idNum, name: row.payment_processor });
    }
  }, [onboardingState, returnProcessorId, processors]);
  // How many rows the query itself matches, ignoring the Active Only /
  // Show unconnected toggles — so a search that "finds nothing" can say
  // whether the row is genuinely absent or just filtered out of view.
  const searchMatchCount = useMemo(
    () => processors.filter((p) => matchesProcessorSearch(p, search)).length,
    [processors, search]
  );
  const hiddenByToggles = Math.max(searchMatchCount - rowData.length, 0);

  const generateColDefs = (): ColDef<PaymentProcessor>[] => [
    {
      headerName: "Id",
      valueGetter: (p) => p.data?.id,
      cellRenderer: createCellRenderer(
        editCreatePermission,
        (p) => `/payment-processors/${p.data.id}/edit`
      ),
      filter: "agTextColumnFilter",
      pinned: "left",
    },
    {
      headerName: "Processor Name",
      valueGetter: (p) => p.data?.payment_processor,
      cellRenderer: createCellRenderer(
        editCreatePermission,
        (p) => `/payment-processors/${p.data.id}/edit`
      ),
      filter: "agTextColumnFilter",
      pinned: "left",
    },
    {
      headerName: "Is Active?",
      valueGetter: (p) => p.data?.is_active,
      valueFormatter: (p) => {
        return p.data?.is_active ? "Yes" : "No";
      },
      filter: "agTextColumnFilter",
      filterValueGetter: (p) => (p.data?.is_active ? "Yes" : "No"),
    },
    {
      headerName: "Processor",
      valueGetter: (p) => p.data?.processor,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Merchant ID",
      valueGetter: (p) => p.data?.config?.merchantId,
      filter: "agTextColumnFilter",
    },
    // RP-2553: the Stripe ids ops look up most, straight from config.
    // platform_account_location_id is the location the POS connects to, for
    // direct and destination-flow venues alike.
    {
      headerName: "Connected Account",
      valueGetter: (p) => p.data?.config?.connected_account_id || "",
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Terminal Location",
      valueGetter: (p) => p.data?.config?.platform_account_location_id || "",
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Events",
      valueGetter: (p) => p.data?.events,
      valueFormatter: (params) => {
        const events: Events[] = params.value || [];
        return events.map((event) => event.name).join(", ");
      },
      cellRenderer: createMultipleLinksCellRenderer(
        eventsProcessorEditPermission,
        (events) => `/events/${events.id}/edit`
      ),
      filter: "agTextColumnFilter",
      filterValueGetter: (p) => {
        const events: Events[] = (p.data?.events || []) as unknown as Events[];
        return events.map((event) => event.name).join(", ");
      },
    },
    {
      headerName: "Fee per Transaction",
      valueGetter: (p) => p.data?.config?.application_fee_per_transaction,
      valueFormatter: (p) => {
        return p.value != null ? `$${Number(p.value).toFixed(2)}` : "";
      },
      filter: "agTextColumnFilter",
      filterValueGetter: (p) => {
        const v = p.data?.config?.application_fee_per_transaction;
        return v != null ? `$${Number(v).toFixed(2)}` : "";
      },
    },
    {
      headerName: "Volume Percentage",
      valueGetter: (p) => p.data?.processing_rate,
      valueFormatter: (p) => {
        return p.value ? `${((p.value || 0) * 100).toFixed(2)}%` : "";
      },
      filter: "agTextColumnFilter",
      filterValueGetter: (p) => {
        const v = p.data?.processing_rate;
        return v ? `${((v || 0) * 100).toFixed(2)}%` : "";
      },
    },
    {
      headerName: "Updated By",
      valueGetter: (p) => p.data?.user?.username,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Created Date",
      valueGetter: (p) => p.data?.created_at,
      valueFormatter: (p) => {
        const date = p.value ? moment(p.value) : null;
        return date ? date.format("MM/DD/YYYY h:mm:ss a") : "";
      },
      filter: "agTextColumnFilter",
      filterValueGetter: (p) =>
        p.data?.created_at ? moment(p.data.created_at).format("MM/DD/YYYY h:mm:ss a") : "",
    },
    {
      headerName: "Updated Date",
      valueGetter: (p) => p.data?.updated_at,
      valueFormatter: (p) => {
        const date = p.value ? moment(p.value) : null;
        return date ? date.format("MM/DD/YYYY h:mm:ss a") : "";
      },
      filter: "agTextColumnFilter",
      filterValueGetter: (p) =>
        p.data?.updated_at ? moment(p.data.updated_at).format("MM/DD/YYYY h:mm:ss a") : "",
    },
  ];
  const colDefs: ColDef<PaymentProcessor>[] = generateColDefs();
  const rowHeight = 50;
  const autoSizeStrategy: SizeColumnsToContentStrategy = {
    type: "fitCellContents",
  };
  const handleExportCSV = () => {
    if (gridRef.current) {
      const timestamp = new Date().toISOString().replace(/[-:]/g, "");
      const params = {
        fileName: `PaymentProcessors_${timestamp}.csv`,
        columnSeparator: ",",
      };
      gridRef.current.exportDataAsCsv(params);
    }
  };
  const gridOptions = {
    enableCellTextSelection: true,
  };
  return (
    <div style={{ height: "75vh" }}>
      <div
        className={"ag-theme-quartz"}
        style={{ width: "100%", height: "100%" }}
      >
        <div className=" grid gap-2 grid-cols-6">
          <Breadcrumbs/>
          <div className="col-span-2 col-start-3 mt-1 flex flex-row items-center justify-center gap-6">
            <div className="flex flex-col items-center">
              <span className="block text-xs font-medium text-gray-500">Active Only</span>
              <Switch
                  checked={isActiveToggle}
                  onChange={() => setIsActiveToggle((prev) => !prev)}
                  onColor="#EE2E30"
                  onHandleColor="#FFFFFF"
                  handleDiameter={18}
                  uncheckedIcon={false}
                  checkedIcon={false}
                  boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                  activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                  height={15}
                  width={48}
              />
            </div>
            <div className="flex flex-col items-center">
              <span className="block text-xs font-medium text-gray-500">Show unconnected</span>
              <Switch
                  checked={showUnconnected}
                  onChange={() => setShowUnconnected((prev) => !prev)}
                  onColor="#EE2E30"
                  onHandleColor="#FFFFFF"
                  handleDiameter={18}
                  uncheckedIcon={false}
                  checkedIcon={false}
                  boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                  activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                  height={15}
                  width={48}
              />
            </div>
          </div>
          <div className="col-span-2 col-start-5 mt-2">
            <Input
                name="search"
                defaultValue={search}
                placeholder={PROCESSOR_SEARCH_PLACEHOLDER}
                onChange={(s) => setSearch(typeof s === "string" ? s : "")}
            />
            {search.trim().length > 0 && (
                <p className="mt-1 text-xs text-gray-500">
                  {rowData.length} of {searchMatchCount}{" "}
                  {searchMatchCount === 1 ? "match" : "matches"} shown
                  {hiddenByToggles > 0 &&
                      ` — ${hiddenByToggles} hidden by the toggles`}
                </p>
            )}
          </div>
          <div className="py-2 col-span-2 col-start-7 flex items-center justify-end gap-2">
            <SecondaryButton onClick={handleExportCSV}>
              Export to CSV
            </SecondaryButton>
            {editCreatePermission && (
                <PrimaryButton
                    onClick={() => navigate(`/payment-processors/create`)}
                >
                  Create
                </PrimaryButton>
            )}
            <NewStripeConnectAccountModal />
          </div>
        </div>
        <AgGridReact
            rowData={rowData}
            columnDefs={colDefs}
            rowHeight={rowHeight}
            autoSizeStrategy={autoSizeStrategy}
            pagination={true}
            gridOptions={gridOptions}
            paginationPageSize={100}
            onGridReady={(params) => (gridRef.current = params.api)}
        />
      </div>
      {postOnboardLocationFor && (
        <TerminalLocationModal
          processorId={postOnboardLocationFor.id}
          processorName={postOnboardLocationFor.name}
          onClose={() => setPostOnboardLocationFor(null)}
          onSaved={() => {
            setPostOnboardLocationFor(null);
            revalidator.revalidate();
          }}
        />
      )}
    </div>
  );
};
